import React, { useDeferredValue, useEffect, useMemo, useState } from "react"
import Table, { TableColumn } from "component/table/table"
import MainTabsTableHeader from "component/mainTabsTableHeader/mainTabsTableHeader"
import { TablePaginationBar, useClientListPagination } from "component/tablePagination"
import CustomizedMenu from "component/muiMenu/muiMenu"
import IconsColor from "util/iconsColor"
import utilStyles from "../../asset/styles/util/util.module.scss"
import { applyFilters, applySearch, cycleSort, sortRowsBy } from "util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "context/listFilterContext"
import { useSnackbar } from "context/snackbarContext"
import { agent } from "apiClient/api"
import {
    CITY_FILTER_ACCESSORS,
    CITY_FILTER_GROUPS,
    ConfigurationCity,
    citySearchFields,
    mapCityApiItemToConfigurationCity,
} from "component/configuration/configurationCities"
import CityPopup from "component/configuration/cityPopup"
import ChangeCityStatusPopup from "component/configuration/changeCityStatusPopup"

const CitiesManagement: React.FC = () => {
    const { showError } = useSnackbar()
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.configurationCities)

    const [cities, setCities] = useState<ConfigurationCity[]>([])
    const [popupOpen, setPopupOpen] = useState(false)
    const [editCity, setEditCity] = useState<ConfigurationCity | null>(null)
    const [statusCity, setStatusCity] = useState<ConfigurationCity | null>(null)

    const fetchCities = async () => {
        try {
            const response = await agent.CityListAPI.get()
            if (response?.status === true) {
                setCities((response?.data ?? []).map(mapCityApiItemToConfigurationCity))
            } else {
                showError("Failed to load cities")
            }
        } catch (err) {
            console.error("Get city list error", err)
            showError("Failed to load cities")
        }
    }

    useEffect(() => {
        fetchCities()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )

    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredCities = useMemo(() => {
        let rows = applyFilters(cities, deferredFilters, CITY_FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, citySearchFields)
        return sortRowsBy(rows, sortState)
    }, [cities, deferredFilters, deferredSearch, sortState])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredCities, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    const openAdd = () => {
        setEditCity(null)
        setPopupOpen(true)
    }

    const openEdit = (city: ConfigurationCity) => {
        setEditCity(city)
        setPopupOpen(true)
    }

    const columns: TableColumn<ConfigurationCity>[] = [
        { key: "city_name", label: "City name", width: "60%", align: "left" },
        {
            key: "flag_active",
            label: "Status",
            width: "20%",
            align: "left",
            render: (value: boolean) => (
                <span className={value ? utilStyles.statusBadgeActive : utilStyles.statusBadgeInactive}>
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "actions",
            label: "Actions",
            width: "20%",
            align: "center",
            render: (_: unknown, row: ConfigurationCity) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <CustomizedMenu
                        buttonText=""
                        menuItems={[
                            {
                                label: "Update City",
                                icon: <IconsColor iconName="editIcon" width="16" height="16" fillColor="none" strokeColor="currentColor" />,
                                onClick: () => openEdit(row),
                            },
                            {
                                label: `Set as ${row.flag_active ? "Inactive" : "Active"}`,
                                icon: <IconsColor iconName="warningSign" width="16" height="16" fillColor="none" strokeColor="currentColor" />,
                                onClick: () => setStatusCity(row),
                            },
                        ]}
                        buttonVariant="text"
                        buttonStyle={{ padding: "0px", minWidth: "0px" }}
                        buttonEndIcon={
                            <IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />
                        }
                    />
                </div>
            ),
        },
    ]

    return (
        <>
            <div className={`${utilStyles.pageContainer} ${utilStyles.listPageFill}`}>
                <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search cities"
                        totalCount={cities.length}
                        filteredCount={filteredCities.length}
                        itemLabel="cities"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={CITY_FILTER_GROUPS}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel="Add new city"
                        onAddClick={openAdd}
                        showResultCount={false}
                    />
                </div>
                <div className={utilStyles.listPageTableBlock}>
                    <div className={utilStyles.listPageTableScroll}>
                        <Table
                            columns={columns}
                            data={pagedRows}
                            keyExtractor={(row) => row.city_id}
                            emptyMessage="No cities available"
                            minWidth={700}
                            fillHeight={true}
                            sortKey={sortState.key}
                            sortDirection={sortState.direction}
                            onSort={handleColumnSort}
                        />
                    </div>
                    <TablePaginationBar
                        total={pagedTotal}
                        page={currentPage}
                        pageSize={currentPageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            </div>
            <CityPopup
                isOpen={popupOpen}
                city={editCity}
                existingCities={cities}
                onClose={() => setPopupOpen(false)}
                onSuccess={fetchCities}
            />
            <ChangeCityStatusPopup
                isOpen={Boolean(statusCity)}
                city={statusCity}
                onCancel={() => setStatusCity(null)}
                onSuccess={fetchCities}
            />
        </>
    )
}

export default CitiesManagement
